import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useHesapAuth } from '../../store/hesapStore';
import { useTheme } from '../../store/themeStore';
import { isHesapSubdomain, hesapPrefix } from '../../lib/subdomain';
import HesapTopbar from './HesapTopbar';
import HesapFooter from './HesapFooter';
import SolarLiveBanner from './SolarLiveBanner';
import { BrandLogo } from './BrandLogo';
import { useHesapBranding } from '../../store/hesapBrandingStore';
import {
  LayoutDashboard, ShoppingCart, Package, PackageOpen, Tag, Users, Building2,
  TrendingUp, LogOut, Menu, X, UserCog, UtensilsCrossed, ChefHat, Sun, Moon, FileText, FileSignature, Calculator, ClipboardList, Wrench, Cloud, Settings as SettingsIcon, CreditCard, Cpu, Globe, Store
} from 'lucide-react';

const prefix = hesapPrefix();

const allNavItems = [
  { path: `${prefix || '/'}`, icon: LayoutDashboard, label: 'Gösterge Paneli', exact: true, perm: 'dashboard' },
  { path: `${prefix}/satis`, icon: ShoppingCart, label: 'POS Satış', perm: 'pos' },
  { path: `${prefix}/satis-rapor`, icon: FileText, label: 'Satış Raporu', perm: 'satis_rapor' },
  { path: `${prefix}/urunler`, icon: Package, label: 'Ürünler', perm: 'urunler' },
  { path: `${prefix}/stok`, icon: PackageOpen, label: 'Stok Giriş/Çıkış', perm: 'stok' },
  { path: `${prefix}/fiyat-gor`, icon: Tag, label: 'Fiyat Gör', perm: 'fiyat_gor' },
  { path: `${prefix}/teklif`, icon: FileSignature, label: 'Teklif Formu', perm: 'teklif' },
  { path: `${prefix}/hesaplama`, icon: Calculator, label: 'Hesaplama Aracı', perm: 'hesaplama' },
  { path: `${prefix}/musteriler`, icon: Users, label: 'Müşteriler / Veresiye', perm: 'musteriler' },
  { path: `${prefix}/gelir-gider`, icon: TrendingUp, label: 'Gelir / Gider', perm: 'gelir_gider' },
  { path: `${prefix}/apartman`, icon: Building2, label: 'Apartman Yönetimi', perm: 'apartman' },
  { path: `${prefix}/yemek-siparis`, icon: UtensilsCrossed, label: 'Yemek Sipariş', perm: 'yemek_siparis' },
  { path: `${prefix}/yemek-mutfak`, icon: ChefHat, label: 'Mutfak / Menü', perm: 'yemek_mutfak' },
  { path: `${prefix}/is-takibi`, icon: ClipboardList, label: 'İş Takibi', perm: 'is_takibi' },
  { path: `${prefix}/teknik-servis`, icon: Wrench, label: 'Teknik Servis', perm: 'teknik_servis' },
  { path: `${prefix}/vitrin-yonetim`, icon: Store, label: 'Ürün Vitrini', perm: 'vitrin' },
  { path: `${prefix}/bulut`, icon: Cloud, label: 'Bulut Depolama', perm: 'bulut' },
  { path: `${prefix}/solar`, icon: Sun, label: 'Solar System', perm: 'solar' },
  { path: `${prefix}/akilli`, icon: Cpu, label: 'Akıllı Takip', perm: 'akilli' },
];

export default function HesapLayout() {
  const { user, logout } = useHesapAuth();
  const { theme, toggle: toggleTheme, init: initTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const brandTitle = useHesapBranding(s => s.title);

  useEffect(() => { initTheme(); }, [initTheme]);

  const handleLogout = useCallback(() => {
    logout();
    navigate(isHesapSubdomain() ? '/giris' : '/hesap/giris');
  }, [logout, navigate]);

  const userPerms = user?.permissions || [];
  const isAdmin = user?.role === 'admin';

  const navItems = allNavItems.filter(item => isAdmin || userPerms.includes(item.perm));

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-slate-700/60 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="flex items-center gap-2.5">
          <BrandLogo testId="sidebar-brand-logo" />
          <div className="min-w-0">
            <h1 className="text-base font-bold text-emerald-400 truncate" data-testid="sidebar-brand-title">{brandTitle}</h1>
            <p className="text-[10px] text-slate-400 truncate">{user?.full_name}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-gradient-to-r from-emerald-600/30 to-transparent text-emerald-300 border-l-4 border-emerald-400 font-semibold'
                  : 'text-slate-300 hover:bg-slate-700/40 hover:text-emerald-200 border-l-4 border-transparent hover:border-emerald-500/40'
              }`
            }
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="px-4 mt-3 mb-1">
              <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Yönetim</p>
            </div>
            <NavLink
              to={`${prefix}/kullanicilar`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600/30 to-transparent text-violet-300 border-l-4 border-violet-400 font-semibold'
                    : 'text-slate-300 hover:bg-slate-700/40 hover:text-violet-200 border-l-4 border-transparent hover:border-violet-500/40'
                }`
              }
            >
              <UserCog className="h-4 w-4 flex-shrink-0" />
              <span>Kullanıcı Yönetimi</span>
            </NavLink>
            <NavLink
              to={`${prefix}/abonelikler`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600/30 to-transparent text-violet-300 border-l-4 border-violet-400 font-semibold'
                    : 'text-slate-300 hover:bg-slate-700/40 hover:text-violet-200 border-l-4 border-transparent hover:border-violet-500/40'
                }`
              }
              data-testid="sidebar-subscriptions-link">
              <CreditCard className="h-4 w-4 flex-shrink-0" />
              <span>Abonelikler</span>
            </NavLink>
            <NavLink
              to={`${prefix}/site-ayarlari`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600/30 to-transparent text-violet-300 border-l-4 border-violet-400 font-semibold'
                    : 'text-slate-300 hover:bg-slate-700/40 hover:text-violet-200 border-l-4 border-transparent hover:border-violet-500/40'
                }`
              }
              data-testid="sidebar-site-settings-link">
              <Globe className="h-4 w-4 flex-shrink-0" />
              <span>Site Ayarları</span>
            </NavLink>
          </>
        )}

        {/* Ayarlar — herkes için */}
        <div className="px-4 mt-3 mb-1">
          <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Hesap</p>
        </div>
        <NavLink
          to={`${prefix}/ayarlar`}
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) =>
            `group flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
              isActive
                ? 'bg-gradient-to-r from-sky-600/30 to-transparent text-sky-300 border-l-4 border-sky-400 font-semibold'
                : 'text-slate-300 hover:bg-slate-700/40 hover:text-sky-200 border-l-4 border-transparent hover:border-sky-500/40'
            }`
          }
          data-testid="sidebar-settings-link">
          <SettingsIcon className="h-4 w-4 flex-shrink-0" />
          <span>Kullanıcı Ayarları</span>
        </NavLink>
      </nav>

      <div className="p-3 border-t border-slate-700/60 space-y-1">
        <button onClick={toggleTheme}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 rounded-lg transition-colors"
          data-testid="sidebar-theme-toggle">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          <span>{theme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}</span>
        </button>
        <button onClick={handleLogout}
          className="group flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-lg transition-colors"
          data-testid="hesap-logout-btn">
          <LogOut className="h-4 w-4" />
          <span>Çıkış</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex" data-testid="hesap-layout">
      {/* Mobile menu button */}
      <button onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-2.5 left-2.5 z-50 bg-slate-800 text-white p-2 rounded-lg shadow-lg active:scale-95 transition-transform"
        aria-label="Menü"
        data-testid="mobile-menu-btn">
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-30 animate-fade-in"
          onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 w-60 sm:w-64 bg-slate-900 z-40 transform transition-transform duration-300 ease-out lg:translate-x-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
      }`}>
        <NavContent />
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen min-w-0 flex flex-col">
        <div className="sticky top-0 z-20 pl-12 lg:pl-0">
          <HesapTopbar />
          <SolarLiveBanner />
        </div>
        <div className="p-3 sm:p-4 lg:p-6 flex-1">
          <Outlet />
        </div>
        <HesapFooter />
      </main>
    </div>
  );
}
